import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Typography,
  Button,
} from "@mui/material";
import * as XLSX from "xlsx-js-style";
import Issues from "./Components/Issues";
import Chat from "./Components/Chat";
import ProgressIndicator from "./Components/ProgressIndicator";
import "./Quality.scss";
import drawerTemplate from "../../hoc/drawerTemplate";
import {
  fetchQualityData,
  processQualityData,
  setupQualityDataListener,
} from "./api/qualityAPI";
import ProjectService from "../../api/projectService";
import structureLocation from "../../newcomponent/structureLocation";
import floorNumberToText from "../../newcomponent/FloorConverter";

const projectService = new ProjectService();

const Quality = () => {
  // State variables
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [qualityData, setQualityData] = useState({});
  const [qualityLoading, setQualityLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [newIssueMode, setNewIssueMode] = useState(false);

  // New states for real-time updates
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncError, setSyncError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Refs for cleanup and caching
  const qualityListenerRef = useRef(null);

  // New states for exporting all sites
  const [exportingAllSites, setExportingAllSites] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportProgressText, setExportProgressText] = useState("");

  // Handle real-time data updates with localStorage size management
  const handleQualityDataUpdate = useCallback(
    (updatedData) => {
      setQualityData(updatedData);
      setQualityLoading(false);

      // Check if localStorage has space before storing
      try {
        // Only store a subset of data in localStorage to avoid quota issues
        if (selectedProject?.ProjectId) {
          // Create a lightweight version of the data for caching
          const lightweightData = {};

          // Process each date group to include only essential data
          Object.entries(updatedData).forEach(([dateKey, dateGroup]) => {
            lightweightData[dateKey] = {
              ...dateGroup,
              items: dateGroup.items.map((item) => ({
                id: item.id,
                location: item.location,
                description: item.description,
                status: item.status,
                lastStatusChangeBy: item.lastStatusChangeBy,
                lastStatusChangeAt: item.lastStatusChangeAt,
                team: item.team,
                by: item.by,
                timestamp: item.timestamp,
                level: item.level,
                type: item.type,
                path: item.path,
                // Exclude large image data to save space
                hasImages: item.images
                  ? Object.keys(item.images).length > 0
                  : false,
              })),
            };
          });

          // Define the cleanup function before using it
          const storageCleanup = () => {
            // Get all keys from localStorage
            const keys = [];
            for (let i = 0; i < localStorage.length; i++) {
              keys.push(localStorage.key(i));
            }

            // Find and remove quality data for other projects
            const qualityDataKeys = keys.filter(
              (key) =>
                key.startsWith("qualityData_") &&
                key !== `qualityData_${selectedProject.ProjectId}`
            );

            // Remove older caches to free up space
            qualityDataKeys.forEach((key) => {
              try {
                localStorage.removeItem(key);
              } catch (e) {
                console.log("Error removing item:", e);
              }
            });
          };

          try {
            // Try to save the lightweight data
            localStorage.setItem(
              `qualityData_${selectedProject.ProjectId}`,
              JSON.stringify(lightweightData)
            );
          } catch (storageError) {
            // If we still hit quota issues, clean up and try again
            console.warn(
              "Storage quota issue, cleaning up and retrying:",
              storageError
            );
            storageCleanup();

            try {
              localStorage.removeItem(
                `qualityData_${selectedProject.ProjectId}`
              );
              localStorage.setItem(
                `qualityData_${selectedProject.ProjectId}`,
                JSON.stringify(lightweightData)
              );
            } catch (retryError) {
              // If it still fails, just log it and continue without caching
              console.error(
                "Failed to cache data even after cleanup:",
                retryError
              );
            }
          }
        }
      } catch (error) {
        console.error("Error updating quality data in cache:", error);
        // Continue without caching - application will still work
      }
    },
    [selectedProject]
  );

  // Handle individual item updates for real-time experience
  const handleItemProcessed = useCallback(
    ({ dateKey, dateHeader, item }) => {
      setQualityData((prevData) => {
        // Create a new data object or use the existing one
        const newData = { ...prevData };

        // Check if the date group exists
        if (!newData[dateKey]) {
          // Create a new date group
          newData[dateKey] = {
            id: dateKey.toString(),
            date: dateHeader.date,
            items: [],
          };
        }

        // Check if the item already exists
        const existingItemIndex = newData[dateKey].items.findIndex(
          (existing) => existing.id === item.id
        );

        if (existingItemIndex >= 0) {
          // Replace existing item
          newData[dateKey].items[existingItemIndex] = item;
        } else {
          // Add new item to the appropriate date group
          newData[dateKey].items.push(item);

          // Sort items by timestamp (newest first)
          newData[dateKey].items.sort(
            (a, b) => parseInt(b.id) - parseInt(a.id)
          );
        }

        return newData;
      });

      // Even when loading individual items, we can reduce the loading indicator as items appear
      if (qualityLoading) {
        setQualityLoading((prev) => {
          // After a few items appear, reduce the "loading" perception
          // but don't hide it completely until the full data load is done
          return prev;
        });
      }
    },
    [qualityLoading]
  );

  // Handle sync progress updates
  const handleSyncProgress = useCallback((progress, error = null) => {
    setSyncProgress(progress);
    setSyncError(error || null);

    // Once we reach 100%, keep the indicator visible for a moment
    if (progress >= 100) {
      setTimeout(() => {
        setIsSyncing(false);
      }, 3000);
    }
  }, []);

  // Handle issue update (used for real-time status changes)
  const handleIssueUpdate = useCallback(
    (updatedIssue) => {
      if (!updatedIssue || !updatedIssue.id) return;

      // Update the issue in qualityData
      setQualityData((prevData) => {
        if (!prevData || Object.keys(prevData).length === 0) return prevData;

        // Create a deep copy of the data to avoid mutation
        const newData = JSON.parse(JSON.stringify(prevData));

        // Find the date group that contains this issue
        for (const dateKey in newData) {
          const dateGroup = newData[dateKey];
          const itemIndex = dateGroup.items.findIndex(
            (item) => item.id === updatedIssue.id
          );

          if (itemIndex !== -1) {
            // Update the issue with new data
            newData[dateKey].items[itemIndex] = {
              ...newData[dateKey].items[itemIndex],
              ...updatedIssue,
            };

            // If we're changing cache, also update localStorage cache
            if (selectedProject?.ProjectId) {
              localStorage.setItem(
                `qualityData_${selectedProject.ProjectId}`,
                JSON.stringify(newData)
              );
            }

            break;
          }
        }

        return newData;
      });
    },
    [selectedProject]
  );

  // Functions for handling issue selection and management
  const handleIssueClick = useCallback((issue) => {
    setSelectedIssue(issue);
    setShowChat(true);
    setNewIssueMode(false);
  }, []);

  // Updated handler to support the new issue creation flow with location selection
  const handleAddNewIssue = useCallback(() => {
    // Set newIssueMode to true, which will tell the Chat component this is a new issue
    setNewIssueMode(true);
    // Create a new empty issue with just the project ID to start with
    setSelectedIssue({
      id: `new_${Date.now()}`,
      status: "Open",
      path: `${selectedProject?.ProjectId}/Quality`,
      timestamp: new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      date: new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      by: getUserName(),
      isNew: true,
    });
    setShowChat(true);
  }, [selectedProject]);

  // Helper function to get the current user's name from localStorage
  const getUserName = () => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        return user.name || "Admin User";
      }
      return "Admin User";
    } catch (error) {
      console.error("Error getting user name:", error);
      return "Admin User";
    }
  };

  const handleCloseChat = useCallback(() => {
    setShowChat(false);
    setSelectedIssue(null);
    setNewIssueMode(false);
  }, []);

  // New function to fetch project structure data and format locations
  const fetchProjectStructure = useCallback(async (projectId) => {
    if (!projectId) return;

    try {
      // First check if we have cached structure data
      const cachedStructure = localStorage.getItem(
        `projectStructure_${projectId}`
      );

      if (cachedStructure) {
        const parsedStructure = JSON.parse(cachedStructure);
        // Use the cached data for immediate display
        setLocationOptions(parsedStructure);
      }

      // Always fetch fresh data from the server
      const response = await projectService.getProjectStructure(projectId);

      if (response && response.status === 200 && response.payload) {
        // Process the project structure into formatted location options
        const formattedLocations = structureLocation(
          response.payload.blocks || {},
          projectId
        );

        // Update state with the formatted locations
        setLocationOptions(formattedLocations);

        // Cache the structured data for future use
        try {
          localStorage.setItem(
            `projectStructure_${projectId}`,
            JSON.stringify(formattedLocations)
          );
        } catch (e) {
          console.warn("Failed to cache project structure:", e);
        }
      }
    } catch (error) {
      console.error("Error fetching project structure:", error);
      setLocationOptions([]);
    }
  }, []);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);

      try {
        // Try to get from cache first
        const cachedProjects = localStorage.getItem("qualityProjects");
        let parsedProjects = [];

        if (cachedProjects) {
          parsedProjects = JSON.parse(cachedProjects);
          setProjects(parsedProjects);

          // Check for last selected project ID first (this is shared across components)
          const lastSelectedProjectId = localStorage.getItem(
            "lastSelectedProject"
          );

          if (lastSelectedProjectId) {
            const foundProject = parsedProjects.find(
              (p) =>
                p.ProjectId === lastSelectedProjectId ||
                p.ProjectId.toString() === lastSelectedProjectId
            );

            if (foundProject) {
              setSelectedProject(foundProject);
              localStorage.setItem(
                "selectedQualityProject",
                JSON.stringify(foundProject)
              );
              setIsLoading(false);

              // We found the project, continue with API calls
              const response = await projectService.getAllProjects();
              if (response.status === 200 && response.payload) {
                const projectsList = response.payload;
                setProjects(projectsList);
                localStorage.setItem(
                  "qualityProjects",
                  JSON.stringify(projectsList)
                );
              }

              return;
            }
          }

          // Fall back to the quality-specific stored project
          const storedProject = localStorage.getItem("selectedQualityProject");
          if (storedProject) {
            const parsedProject = JSON.parse(storedProject);
            setSelectedProject(parsedProject);
            // Also ensure the lastSelectedProject ID is set for cross-component consistency
            localStorage.setItem(
              "lastSelectedProject",
              parsedProject.ProjectId.toString()
            );
          } else if (parsedProjects.length > 0) {
            setSelectedProject(parsedProjects[0]);
            localStorage.setItem(
              "selectedQualityProject",
              JSON.stringify(parsedProjects[0])
            );
            localStorage.setItem(
              "lastSelectedProject",
              parsedProjects[0].ProjectId.toString()
            );
          }

          setIsLoading(false);
        }

        // Fetch latest projects from API
        const response = await projectService.getAllProjects();

        if (response.status === 200 && response.payload) {
          const projectsList = response.payload;
          setProjects(projectsList);

          // Cache projects
          localStorage.setItem("qualityProjects", JSON.stringify(projectsList));

          // If no project is selected yet, select the first one or find the last selected one
          if (!selectedProject) {
            const lastSelectedProjectId = localStorage.getItem(
              "lastSelectedProject"
            );

            if (lastSelectedProjectId) {
              const foundProject = projectsList.find(
                (p) =>
                  p.ProjectId === lastSelectedProjectId ||
                  p.ProjectId.toString() === lastSelectedProjectId
              );

              if (foundProject) {
                setSelectedProject(foundProject);
                localStorage.setItem(
                  "selectedQualityProject",
                  JSON.stringify(foundProject)
                );
                return;
              }
            }

            if (projectsList.length > 0) {
              setSelectedProject(projectsList[0]);
              localStorage.setItem(
                "selectedQualityProject",
                JSON.stringify(projectsList[0])
              );
              localStorage.setItem(
                "lastSelectedProject",
                projectsList[0].ProjectId.toString()
              );
            }
          }
        } else {
          console.error("Failed to fetch projects:", response.statusMsg);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Set up real-time listener when project changes
  useEffect(() => {
    if (!selectedProject?.ProjectId) return;

    setQualityLoading(true);
    setSyncProgress(0);
    setSyncError(null);
    setIsSyncing(true);

    // Clean up any existing listener
    if (qualityListenerRef.current) {
      qualityListenerRef.current.unsubscribe();
      qualityListenerRef.current = null;
    }

    const setupQualityData = async () => {
      try {
        // Try loading from cache first for immediate display
        const cachedData = localStorage.getItem(
          `qualityData_${selectedProject.ProjectId}`
        );

        if (cachedData) {
          setQualityData(JSON.parse(cachedData));
        }

        // Set up real-time listener
        qualityListenerRef.current = setupQualityDataListener(
          selectedProject.ProjectId,
          handleQualityDataUpdate,
          handleSyncProgress,
          handleItemProcessed // Add our item processor to handle individual updates
        );

        // Fetch location options for the selected project
      } catch (error) {
        console.error("Error setting up quality data:", error);
        setSyncError(error);
        setQualityLoading(false);
        setIsSyncing(false);
      }
    };

    setupQualityData();

    // Clean up listener on unmount or when project changes
    return () => {
      if (qualityListenerRef.current) {
        qualityListenerRef.current.unsubscribe();
        qualityListenerRef.current = null;
      }
    };
  }, [selectedProject, handleQualityDataUpdate, handleSyncProgress]);

  // Project selection handler
  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    localStorage.setItem("selectedQualityProject", JSON.stringify(project));
    localStorage.setItem("lastSelectedProject", project.ProjectId.toString());
    // Reset selected location when project changes
    setSelectedLocation("");
  };

  // Handle refresh functionality
  const handleRefreshData = async () => {
    if (!selectedProject?.ProjectId) return;

    // Reset states for refresh
    setQualityLoading(true);
    setSyncProgress(0);
    setSyncError(null);
    setIsSyncing(true);

    try {
      // Remove cache for this project to ensure fresh data
      localStorage.removeItem(`qualityData_${selectedProject.ProjectId}`);

      // Also refresh location data
      // fetchProjectStructure(selectedProject.ProjectId);

      // Clean up any existing listener
      if (qualityListenerRef.current) {
        qualityListenerRef.current.unsubscribe();
        qualityListenerRef.current = null;
      }

      // Create a new listener
      qualityListenerRef.current = setupQualityDataListener(
        selectedProject.ProjectId,
        handleQualityDataUpdate,
        handleSyncProgress
      );
    } catch (error) {
      console.error("Error refreshing quality data:", error);
      setSyncError(error);
      setQualityLoading(false);
      setIsSyncing(false);
    }
  };

  // Export quality data to Excel with modern sleek styling
  const handleExportToExcel = useCallback(() => {
    if (
      !qualityData ||
      Object.keys(qualityData).length === 0 ||
      !selectedProject
    )
      return;

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Extract quality items from the data structure
    const qualityItems = [];

    Object.entries(qualityData).forEach(([dateKey, dateGroup]) => {
      if (dateGroup && dateGroup.items && Array.isArray(dateGroup.items)) {
        const formattedDate = new Date(parseInt(dateKey)).toLocaleDateString();

        dateGroup.items.forEach((item) => {
          if (item && typeof item === "object") {
            qualityItems.push({
              date: formattedDate,
              location: item.location || "N/A",
              description: item.description || "N/A",
              status: item.status || "N/A",
              reporter: item.by || "N/A",
              lastStatusChangeBy: item.lastStatusChangeBy || "N/A",
              lastStatusChangeAt: item.lastStatusChangeAt || "N/A",
            });
          }
        });
      }
    });

    // Sort quality items by date (newest first)
    qualityItems.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Calculate summary statistics
    const totalIssues = qualityItems.length;
    const openIssues = qualityItems.filter(
      (item) => (item.status || "").toLowerCase() === "open"
    ).length;
    const closedIssues = qualityItems.filter(
      (item) => (item.status || "").toLowerCase() === "closed"
    ).length;

    // Modern color palette - subtle, professional colors
    const colors = {
      // UI colors - sophisticated modern palette
      background: "FFFFFF",
      headerBg: "F8FAFC",
      titleBg: "EEF2FF",
      accentBlue: "3B82F6",
      accentBluePale: "EFF6FF",
      borderLight: "E2E8F0",
      borderMedium: "CBD5E0",
      tableBorder: "E5E7EB",
      altRow: "F9FAFB",

      // Text colors
      textDark: "1E293B", // Almost black
      textMedium: "475569", // Dark gray
      textLight: "64748B", // Medium gray
      textWhite: "FFFFFF", // White

      // Status colors - muted professional
      openStatus: "FF8282", // Red
      inProgressStatus: "EA580C", // Orange
      closedStatus: "A0C878", // Green
      otherStatus: "6B7280", // Gray

      // Summary section
      summaryHeaderBg: "F1F5F9",
      summaryCountsBg: "FAFAFA",
    };

    // Calculate the row where to start the main data table (after the header)
    // We'll use a cleaner, more modern layout with less vertical space
    let rowIndex = 0;

    // Create base worksheet with empty cells
    const ws = XLSX.utils.aoa_to_sheet([[""]]);

    // Space for logo/brand
    XLSX.utils.sheet_add_aoa(ws, [[""]], { origin: { r: rowIndex++, c: 0 } });

    // Title section with the company and project name
    XLSX.utils.sheet_add_aoa(ws, [["Quality Report"]], {
      origin: { r: rowIndex++, c: 0 },
    });
    XLSX.utils.sheet_add_aoa(ws, [[selectedProject.ClientName]], {
      origin: { r: rowIndex++, c: 0 },
    });

    // Small spacing
    rowIndex++;

    // Date and export info - more subtle
    const exportDate = new Date().toLocaleDateString();
    const exportTime = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    XLSX.utils.sheet_add_aoa(
      ws,
      [[`Generated: ${exportDate} at ${exportTime}`]],
      { origin: { r: rowIndex++, c: 0 } }
    );

    // Space before table
    rowIndex++;

    // Define our table headers - clear, concise labels
    const tableHeaders = [
      "Date",
      "Location",
      "Description",
      "Status",
      "Reporter",
      "Last Updated By",
      "Last Updated Time",
    ];

    // Add table headers
    const tableHeaderRow = rowIndex++;
    XLSX.utils.sheet_add_aoa(ws, [tableHeaders], {
      origin: { r: tableHeaderRow, c: 0 },
    });

    // Add data rows
    const dataStartRow = rowIndex;
    if (qualityItems.length > 0) {
      const dataRows = qualityItems.map((item) => [
        item.date,
        item.location,
        item.description,
        item.status,
        item.reporter,
        item.lastStatusChangeBy,
        item.lastStatusChangeAt,
      ]);
      XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: { r: rowIndex, c: 0 } });
      rowIndex += dataRows.length;
    } else {
      // No data message
      XLSX.utils.sheet_add_aoa(
        ws,
        [["No quality data found for this project"]],
        {
          origin: { r: rowIndex++, c: 0 },
        }
      );
    }

    // Add spacing before the summary section
    rowIndex += 2;

    // Summary section - header
    const summaryHeaderRow = rowIndex++;
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], {
      origin: { r: summaryHeaderRow, c: 0 },
    });

    // Summary data with clear layout - only Total, Open and Closed
    const summaryStartRow = rowIndex;
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Total Issues", totalIssues.toString()],
        ["Open", openIssues.toString()],
        ["Closed", closedIssues.toString()],
      ],
      { origin: { r: summaryStartRow, c: 0 } }
    );
    rowIndex += 3; // Reduced from 5 to 3 items

    // Small spacing
    rowIndex++;

    // Footer with branding - subtle
    const footerRow = rowIndex++;
    XLSX.utils.sheet_add_aoa(ws, [["Powered by Staart Buildtech"]], {
      origin: { r: footerRow, c: 0 },
    });

    // Set column widths for better readability and modern layout
    ws["!cols"] = [
      { wch: 12 }, // Date - slightly narrower
      { wch: 25 }, // Location - just right size
      { wch: 70 }, // Description - wide for content
      { wch: 12 }, // Status - just right for status
      { wch: 15 }, // Reporter
      { wch: 15 }, // Last Updated By
      { wch: 15 }, // Last Updated Time
    ];

    // Define merges for headers and titles - making them span across
    ws["!merges"] = [
      // Title
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }, // Quality Report
      { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } }, // Project name

      // Date generated
      { s: { r: 4, c: 0 }, e: { r: 4, c: 6 } },

      // Summary header
      { s: { r: summaryHeaderRow, c: 0 }, e: { r: summaryHeaderRow, c: 1 } },

      // Footer
      { s: { r: footerRow, c: 0 }, e: { r: footerRow, c: 6 } },
    ];

    // Helper function to safely apply styles to cells
    const safeStyleCell = (cellRef, style) => {
      if (!ws[cellRef]) {
        const match = cellRef.match(/([A-Z]+)([0-9]+)/);
        if (match) {
          const col = match[1];
          const row = parseInt(match[2]) - 1;
          XLSX.utils.sheet_add_aoa(ws, [[""]], { origin: cellRef });
        } else {
          return;
        }
      }
      ws[cellRef].s = style;
    };

    // Create a helper to style entire rows
    const styleRow = (rowNum, style) => {
      for (let i = 0; i < 7; i++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowNum, c: i });
        safeStyleCell(cellRef, style);
      }
    };

    // Apply modern styling

    // 1. Title styling - clean, prominent but not overwhelming
    const titleStyle = {
      font: {
        name: "Arial",
        sz: 16,
        bold: true,
        color: { rgb: colors.accentBlue },
      },
      alignment: { horizontal: "center", vertical: "center" },
      fill: { fgColor: { rgb: colors.titleBg }, patternType: "solid" },
      border: { bottom: { style: "thin", color: { rgb: colors.borderLight } } },
    };
    safeStyleCell("A2", titleStyle);

    // Project name - slightly smaller
    const projectNameStyle = {
      font: {
        name: "Arial",
        sz: 12,
        bold: true,
        color: { rgb: colors.textDark },
      },
      alignment: { horizontal: "center", vertical: "center" },
      fill: { fgColor: { rgb: colors.titleBg }, patternType: "solid" },
      border: { bottom: { style: "thin", color: { rgb: colors.borderLight } } },
    };
    safeStyleCell("A3", projectNameStyle);

    // Date generated - subtle, smaller
    const dateStyle = {
      font: {
        name: "Arial",
        sz: 9,
        italic: true,
        color: { rgb: colors.textLight },
      },
      alignment: { horizontal: "right", vertical: "center" },
    };
    safeStyleCell("A5", dateStyle);

    // Table Headers - clean, modern blue background
    const headerStyle = {
      font: {
        name: "Arial",
        sz: 10,
        bold: true,
        color: { rgb: colors.textWhite },
      },
      fill: { fgColor: { rgb: colors.accentBlue }, patternType: "solid" },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: colors.accentBlue } },
        bottom: { style: "thin", color: { rgb: colors.accentBlue } },
        left: { style: "thin", color: { rgb: colors.accentBlue } },
        right: { style: "thin", color: { rgb: colors.accentBlue } },
      },
    };

    // Apply header styles
    for (let i = 0; i < tableHeaders.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: tableHeaderRow, c: i });
      safeStyleCell(cellRef, headerStyle);
    }

    // Style the data rows with alternating background and proper alignment
    const dataRowBaseStyle = {
      font: { name: "Arial", sz: 9, color: { rgb: colors.textDark } },
      border: {
        top: { style: "thin", color: { rgb: colors.tableBorder } },
        bottom: { style: "thin", color: { rgb: colors.tableBorder } },
        left: { style: "thin", color: { rgb: colors.tableBorder } },
        right: { style: "thin", color: { rgb: colors.tableBorder } },
      },
    };

    // Apply styles to each data row
    for (let i = 0; i < qualityItems.length; i++) {
      const isEven = i % 2 === 0;
      const baseRowStyle = {
        ...dataRowBaseStyle,
        fill: isEven
          ? { fgColor: { rgb: colors.altRow }, patternType: "solid" }
          : { fgColor: { rgb: colors.background }, patternType: "solid" },
      };

      // Apply style to each cell in row
      for (let j = 0; j < 7; j++) {
        const cellRef = XLSX.utils.encode_cell({ r: dataStartRow + i, c: j });
        let cellStyle = { ...baseRowStyle };

        // Special handling for specific columns
        if (j === 2) {
          // Description - left alignment
          cellStyle.alignment = {
            horizontal: "left",
            vertical: "center",
            wrapText: true,
          };
        } else if (j === 3) {
          // Status - special styling with color
          const status = (qualityItems[i].status || "").toLowerCase();
          let statusColor;

          switch (status) {
            case "open":
              statusColor = colors.openStatus;
              break;
            case "in progress":
              statusColor = colors.inProgressStatus;
              break;
            case "closed":
              statusColor = colors.closedStatus;
              break;
            default:
              statusColor = colors.otherStatus;
          }

          cellStyle = {
            ...baseRowStyle,
            font: {
              name: "Arial",
              sz: 9,
              bold: true,
              color: { rgb: colors.textDark },
            },
            fill: { fgColor: { rgb: statusColor }, patternType: "solid" },
            alignment: { horizontal: "center", vertical: "center" },
          };
        } else {
          // Other columns - center alignment
          cellStyle.alignment = {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          };
        }

        safeStyleCell(cellRef, cellStyle);
      }
    }

    // Style summary section - modern, clean design
    const summaryHeaderStyle = {
      font: {
        name: "Arial",
        sz: 10,
        bold: true,
        color: { rgb: colors.textMedium },
      },
      fill: { fgColor: { rgb: colors.summaryHeaderBg }, patternType: "solid" },
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: colors.borderMedium } },
        bottom: { style: "thin", color: { rgb: colors.borderMedium } },
      },
    };
    safeStyleCell(
      XLSX.utils.encode_cell({ r: summaryHeaderRow, c: 0 }),
      summaryHeaderStyle
    );
    safeStyleCell(
      XLSX.utils.encode_cell({ r: summaryHeaderRow, c: 1 }),
      summaryHeaderStyle
    );

    // Style summary rows
    for (let i = 0; i < 3; i++) {
      const labelCellRef = XLSX.utils.encode_cell({
        r: summaryStartRow + i,
        c: 0,
      });
      const valueCellRef = XLSX.utils.encode_cell({
        r: summaryStartRow + i,
        c: 1,
      });

      // Label styling
      safeStyleCell(labelCellRef, {
        font: { name: "Arial", sz: 9, color: { rgb: colors.textMedium } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          bottom: { style: "thin", color: { rgb: colors.borderLight } },
        },
        fill: {
          fgColor: { rgb: colors.summaryCountsBg },
          patternType: "solid",
        },
      });

      // Value styling with appropriate status color for the counts
      let valueFillColor;
      switch (i) {
        case 0:
          valueFillColor = colors.accentBluePale; // Total
          break;
        case 1:
          valueFillColor = colors.openStatus; // Open
          break;
        case 2:
          valueFillColor = colors.closedStatus; // Closed
          break;
      }

      safeStyleCell(valueCellRef, {
        font: {
          name: "Arial",
          sz: 9,
          bold: true,
          color: { rgb: i === 0 ? colors.textDark : colors.textWhite },
        },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: valueFillColor }, patternType: "solid" },
        border: {
          bottom: { style: "thin", color: { rgb: colors.borderLight } },
        },
      });
    }

    // Footer with subtle branding
    safeStyleCell(XLSX.utils.encode_cell({ r: footerRow, c: 0 }), {
      font: {
        name: "Arial",
        sz: 8,
        italic: true,
        color: { rgb: colors.textLight },
      },
      alignment: { horizontal: "center", vertical: "center" },
    });

    // Set print settings for professional printing
    ws["!printHeader"] = [1, 1]; // Repeat first row as header on all pages

    // Add worksheet to workbook with a clean name
    XLSX.utils.book_append_sheet(wb, ws, "Quality Report");

    // Generate Excel file with project name and date in the filename
    const cleanProjectName = selectedProject.ClientName.replace(
      /[^a-zA-Z0-9]/g,
      "_"
    );
    const dateStr = new Date().toISOString().split("T")[0];
    const fileName = `Quality_Report_${cleanProjectName}_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);
  }, [qualityData, selectedProject]);

  // Export all sites' quality data to Excel
  const handleExportAllSitesToExcel = useCallback(async () => {
    setExportingAllSites(true);
    setExportProgress(0);
    setExportProgressText("Fetching projects...");
    try {
      // 1. Fetch all projects
      const response = await projectService.getAllProjects();
      if (!response || response.status !== 200 || !response.payload) {
        alert("Failed to fetch all projects");
        setExportingAllSites(false);
        return;
      }
      const allProjects = response.payload;
      if (!Array.isArray(allProjects) || allProjects.length === 0) {
        alert("No projects found");
        setExportingAllSites(false);
        return;
      }
      // 2. For each project, fetch and process quality data
      const allSiteSummaries = [];
      let completed = 0;
      for (const project of allProjects) {
        setExportProgressText(`Processing: ${project.ClientName}`);
        const qRes = await fetchQualityData(project.ProjectId);
        if (!qRes || !qRes.data) {
          completed++;
          setExportProgress(Math.round((completed / allProjects.length) * 100));
          continue;
        }
        const processed = processQualityData(qRes.data, project.ProjectId);
        // Count total, open, closed for this site
        let total = 0,
          open = 0,
          closed = 0;
        Object.values(processed).forEach((dateGroup) => {
          (dateGroup.items || []).forEach((item) => {
            if (!item.status) return;
            total++;
            if (item.status.toLowerCase() === "open") open++;
            if (item.status.toLowerCase() === "closed") closed++;
          });
        });
        allSiteSummaries.push({
          site: project.ClientName,
          total,
          open,
          closed,
        });
        completed++;
        setExportProgress(Math.round((completed / allProjects.length) * 100));
      }
      setExportProgressText("Generating Excel file...");
      // 3. Generate Excel file in a modern, aligned format
      const wb = XLSX.utils.book_new();
      const wsData = [
        ["STAART APP QUALITY ISSUES"],
        [""],
        ["Site", "Total", "Open issues", "Closed issues"],
        ...allSiteSummaries.map((row) => [
          row.site,
          row.total,
          row.open,
          row.closed,
        ]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      // Set column widths
      ws["!cols"] = [{ wch: 30 }, { wch: 10 }, { wch: 14 }, { wch: 16 }];
      // Merge title row
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
      // Style cells
      const range = XLSX.utils.decode_range(ws["!ref"]);
      // Title style
      ws["A1"].s = {
        font: { name: "Arial", sz: 16, bold: true, color: { rgb: "3B82F6" } },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "EEF2FF" } },
      };
      // Header style
      for (let c = 0; c <= 3; c++) {
        const cell = XLSX.utils.encode_cell({ r: 2, c });
        if (!ws[cell]) continue;
        ws[cell].s = {
          font: { name: "Arial", sz: 11, bold: true, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "3B82F6" } },
          border: {
            top: { style: "thin", color: { rgb: "CBD5E0" } },
            bottom: { style: "thin", color: { rgb: "CBD5E0" } },
            left: { style: "thin", color: { rgb: "CBD5E0" } },
            right: { style: "thin", color: { rgb: "CBD5E0" } },
          },
        };
      }
      // Data row style
      for (let r = 3; r <= range.e.r; r++) {
        const isEven = (r - 3) % 2 === 0;
        for (let c = 0; c <= 3; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });
          if (!ws[cell]) continue;
          ws[cell].s = {
            font: { name: "Arial", sz: 10, color: { rgb: "1E293B" } },
            alignment: {
              horizontal: c === 0 ? "left" : "center",
              vertical: "center",
            },
            fill: { fgColor: { rgb: isEven ? "F9FAFB" : "FFFFFF" } },
            border: {
              top: { style: "thin", color: { rgb: "E5E7EB" } },
              bottom: { style: "thin", color: { rgb: "E5E7EB" } },
              left: { style: "thin", color: { rgb: "E5E7EB" } },
              right: { style: "thin", color: { rgb: "E5E7EB" } },
            },
          };
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, "All Sites Quality");
      const dateStr = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `All_Sites_Quality_${dateStr}.xlsx`);
      setExportProgress(100);
      setExportProgressText("Export complete!");
      setTimeout(() => setExportingAllSites(false), 1200);
    } catch (err) {
      alert("Failed to export all sites data");
      setExportingAllSites(false);
      console.error(err);
    }
  }, []);

  return (
    <Box className="quality-main-container">
      <Box className={`quality-issues-panel ${showChat ? "compact-view" : ""}`}>
        <Issues
          onIssueClick={handleIssueClick}
          onAddNew={handleAddNewIssue}
          compactView={showChat}
          isLoading={qualityLoading || isLoading}
          qualityData={qualityData}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          projects={projects}
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
          onRefresh={handleRefreshData}
          locationOptions={locationOptions}
          onExportToExcel={handleExportToExcel}
          onExportAllSites={handleExportAllSitesToExcel}
        />
      </Box>

      <Box className={`quality-chat-panel ${showChat ? "open" : "closed"}`}>
        {showChat && (
          <Chat
            issue={selectedIssue}
            onClose={handleCloseChat}
            projectId={selectedProject?.ProjectId}
            onIssueUpdate={handleIssueUpdate}
            isNewIssue={newIssueMode}
            locationOptions={locationOptions}
          />
        )}
      </Box>

      {/* Progress indicator for real-time updates */}
      <ProgressIndicator
        progress={syncProgress}
        isVisible={isSyncing || syncError}
        error={syncError}
      />

      {/* Export All Sites Progress Dialog */}
      <Dialog open={exportingAllSites} maxWidth="xs" fullWidth>
        <DialogTitle>Exporting All Sites Data</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 2,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ width: "100%", mb: 2 }}
            />
            <Typography variant="body2" sx={{ mb: 1 }}>
              {exportProgressText}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This may take a few moments for many sites.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default drawerTemplate(Quality);
